import React, { forwardRef, Ref } from "react"
import { Form, FormikProps } from "formik"
import { Formik } from "../../../../../shared/components/forms/SchemaFormik"
import { ProductGroup, ProductGroupFormValues } from "../productGroupTypes"
import useProductGroupValidationSchema from "../hooks/useProductGroupValidationSchema"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { FormikTextField } from "../../../../../shared/components/forms/Formik"
import { SelectProductGroupCode } from "./SelectProductGroupCode"
import { ProductGroupFormSettings } from "./ProductGroupFormSettings"
import { StandardForm } from "../../../../../shared/components/forms/StandardForm"
import { StandardMainPanel } from "../../../../../shared/components/forms/StandardMainPanel"
import { StandardSidePanel } from "../../../../../shared/components/forms/StandardSidePanel"
import { ProductGroupFormAvailableTime } from "./ProductGroupFormAvailableTime"
import { ProductGroupHistory } from "./ProductGroupHistory"
import { FormikPriceListField } from "../../products/components/FormikPriceListsField"

type CreateOrEditProductGroupFormProps = Readonly<{
    initialValues: ProductGroupFormValues
    history?: ProductGroup["history"]
    onSubmit: (formData: ProductGroupFormValues) => void
}>

type FormikRef = Ref<FormikProps<ProductGroupFormValues>>

export const CreateUpdateProductGroupForm = forwardRef(
    ({ initialValues, onSubmit, history }: CreateOrEditProductGroupFormProps, ref: FormikRef) => {
        const validationSchema = useProductGroupValidationSchema()

        function handleSubmit(values: ProductGroupFormValues) {
            onSubmit(values)
        }

        return (
            <Formik<ProductGroupFormValues>
                enableReinitialize
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
                innerRef={ref}
            >
                {({ setFieldValue }) => (
                    <Form>
                        <StandardForm>
                            <StandardMainPanel>
                                <LocalizedStrict id="product-group-form-name-field" attrs={{ label: true }}>
                                    <FormikTextField<ProductGroupFormValues> name="name" label="Name" fullWidth />
                                </LocalizedStrict>
                                {/* <FormikPriceListsField<ProductGroupFormValues> name="priceLists" /> */}
                                <FormikPriceListField<ProductGroupFormValues> name="priceList" />
                                <SelectProductGroupCode />
                            </StandardMainPanel>

                            <LocalizedStrict id="product-group-form-settings-panel" attrs={{ title: true }}>
                                <StandardSidePanel title="Settings">
                                    <ProductGroupFormSettings />
                                </StandardSidePanel>
                            </LocalizedStrict>

                            <LocalizedStrict
                                id="product-group-form-limited-availability-panel-title"
                                attrs={{ title: true, offContent: true }}
                            >
                                <StandardSidePanel
                                    title="Limited availability"
                                    variant="switchable"
                                    offContent="Products in this group are always available for purchase"
                                    checked={initialValues.availableTime}
                                    onCheckChange={(checked) => {
                                        setFieldValue("availableTime", checked)
                                    }}
                                >
                                    <ProductGroupFormAvailableTime />
                                </StandardSidePanel>
                            </LocalizedStrict>

                            {history && (
                                <LocalizedStrict id="history-card" attrs={{ title: true }}>
                                    <StandardSidePanel title="History">
                                        <ProductGroupHistory history={history} />
                                    </StandardSidePanel>
                                </LocalizedStrict>
                            )}
                        </StandardForm>
                    </Form>
                )}
            </Formik>
        )
    },
)
